import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { 
    getCompetencyAPI, 
    createCompetencyAPI,
    Competency
} from "@/Data/User";
import { userDatabase } from "@/Data/User/Database";
import { randomString } from "@/Functions";
import { CompetencyState } from "./type";
import { CreateCompetencyParams, GetCompetencyParams } from "ai-api/types/competency";

// Validation function for competency evaluation data
const validateCompetency = (comp: any): comp is Competency => {
    return comp && 
           typeof comp.evaluationid === 'string' && 
           typeof comp.evaluate_at === 'number' && 
           comp.scores &&
           typeof comp.scores === 'object' &&
           'knowledge' in comp.scores &&
           'understanding' in comp.scores &&
           'expression' in comp.scores;
};

// Atom for managing competency state
const competencyState = atom<CompetencyState>({
    key: 'competencyState'+randomString(4),
    default: {
        competencies: [],
        loading: false,
        error: null
    },
    effects: [
        ({ setSelf }) => {
            const initDatabaseAndLoadCompetencies = async () => {
                try {
                    await userDatabase.init();
                    
                    // Load cached competencies
                    const cached = await userDatabase.getUserCompetencies();
                    
                    // Validate competencies before using
                    const validCompetencies = cached.filter(validateCompetency);
                    
                    if (validCompetencies.length !== cached.length) {
                        console.warn(`Filtered out ${cached.length - validCompetencies.length} invalid competency records from database`);
                    }
                    
                    setSelf({
                        competencies: validCompetencies,
                        loading: false,
                        error: null
                    });
                    
                } catch (error) {
                    console.error('Failed to initialize competency data:', error);
                    setSelf({
                        competencies: [],
                        loading: false,
                        error: 'Failed to load competency data'
                    });
                }
            };
            
            initDatabaseAndLoadCompetencies();
        }
    ]
});

/**
 * Hook for managing user competency evaluations
 */
export const useCompetency = () => {
    const [state, setState] = useRecoilState(competencyState);
    const updateState = useCallback((newState: Partial<CompetencyState>) => {
        setState(prev => ({ ...prev, ...newState }));
    }, [setState]);
    
    const { competencies, loading, error } = state;
    
    /**
     * Fetch competency evaluations from API and update local database
     */
    const fetchCompetencies = useCallback(async (params: GetCompetencyParams = {}) => {
        updateState({ loading: true, error: null });
        
        try {
            // Get data from API
            const results = await getCompetencyAPI(params);
            
            if (!results || !Array.isArray(results)) {
                updateState({ 
                    loading: false, 
                    error: 'No competency data available' 
                });
                return;
            }
            
            // Validate results
            const validResults = results.filter(validateCompetency);
            
            if (validResults.length !== results.length) {
                console.warn(`Filtered out ${results.length - validResults.length} invalid competency records from API`);
            }
            
            // Save to local database
            await userDatabase.saveCompetencies(validResults);
            
            // Update state with new data
            setState(prev => ({
                ...prev,
                competencies: [
                    ...validResults,
                    ...prev.competencies.filter(c => 
                        !validResults.some(vr => vr.evaluationid === c.evaluationid)
                    )
                ].sort((a, b) => b.evaluate_at - a.evaluate_at),
                loading: false,
                error: null
            }));
            
        } catch (error) {
            console.error('Error fetching competency data:', error);
            updateState({ 
                loading: false, 
                error: error instanceof Error ? error.message : 'Failed to fetch competency data' 
            });
        }
    }, [setState, updateState]);
    
    /**
     * Create a new competency evaluation
     */
    const createCompetency = useCallback(async (params: CreateCompetencyParams) => {
        updateState({ loading: true, error: null });
        
        try {
            const result = await createCompetencyAPI(params);
            
            if (!result) {
                updateState({ 
                    loading: false,
                    error: 'Failed to create competency evaluation'
                });
                return null;
            }
            
            // Validate result
            if (!validateCompetency(result)) {
                console.error('Received invalid competency data from API:', result);
                updateState({
                    loading: false,
                    error: 'Invalid competency data received'
                });
                return null;
            }
            
            // Save to local database
            await userDatabase.saveCompetencies([result]);
            
            // Update state with new data
            setState(prev => ({
                ...prev,
                competencies: [result, ...prev.competencies],
                loading: false,
                error: null
            }));
            
            return result;
            
        } catch (error) {
            console.error('Error creating competency evaluation:', error);
            updateState({
                loading: false,
                error: error instanceof Error ? error.message : 'Failed to create competency evaluation'
            });
            return null;
        }
    }, [setState, updateState]);
    
    const reset = useResetRecoilState(competencyState);
    
    return {
        // Data
        competencies,
        loading,
        error,
        
        // API functions
        fetchCompetencies,
        createCompetency,
        
        // Reset state
        reset
    };
};